import { HEX_DIRECTIONS, hexCorners, hexDistance, hexKey, hexToPixel, hexesInRadius, type Hex } from './hex';

export interface VertexInfo {
  /** Position for a hex circumradius of 1, board centered on the origin. */
  x: number;
  y: number;
  hexes: number[];
  edges: number[];
  neighbors: number[];
}

export interface EdgeInfo {
  vertices: [number, number];
  hexes: number[];
}

/**
 * Board graph for one set of land hexes. Game state refers to hexes, vertices,
 * and edges by their index here, so the state itself stays small and
 * JSON-friendly. Each board stores its land hexes, and its topology is derived
 * (and cached) from them; see `topologyOf`.
 */
export interface Topology {
  hexes: Hex[];
  /** Six vertex ids per hex, clockwise from the top corner. */
  hexVertices: number[][];
  /** Six edge ids per hex; edge i joins corner i and corner i+1. */
  hexEdges: number[][];
  hexNeighbors: number[][];
  vertices: VertexInfo[];
  edges: EdgeInfo[];
  /** Coastal edges (one adjacent land hex, including lake shores) clockwise around the board's center, starting near the top. */
  coast: number[];
  /** Hex indices spiralling inward from the outer ring, used to lay out number tokens. */
  spiral: number[];
}

function buildTopology(hexes: readonly Hex[]): Topology {
  const hexIndex = new Map(hexes.map((h, i) => [hexKey(h), i]));

  const vertices: VertexInfo[] = [];
  const vertexByPoint = new Map<string, number>();
  const hexVertices = hexes.map((h, hi) =>
    hexCorners(hexToPixel(h, 1), 1).map((p) => {
      const key = `${Math.round(p.x * 1000)},${Math.round(p.y * 1000)}`;
      let id = vertexByPoint.get(key);
      if (id === undefined) {
        id = vertices.length;
        vertexByPoint.set(key, id);
        vertices.push({ x: p.x, y: p.y, hexes: [], edges: [], neighbors: [] });
      }
      vertices[id]!.hexes.push(hi);
      return id;
    }),
  );

  const edges: EdgeInfo[] = [];
  const edgeByPair = new Map<string, number>();
  const hexEdges = hexVertices.map((corners, hi) =>
    corners.map((a, i) => {
      const b = corners[(i + 1) % 6]!;
      const key = a < b ? `${a},${b}` : `${b},${a}`;
      let id = edgeByPair.get(key);
      if (id === undefined) {
        id = edges.length;
        edgeByPair.set(key, id);
        edges.push({ vertices: a < b ? [a, b] : [b, a], hexes: [] });
        vertices[a]!.edges.push(id);
        vertices[b]!.edges.push(id);
        vertices[a]!.neighbors.push(b);
        vertices[b]!.neighbors.push(a);
      }
      edges[id]!.hexes.push(hi);
      return id;
    }),
  );

  const hexNeighbors = hexes.map((h) =>
    HEX_DIRECTIONS.map((d) => hexIndex.get(hexKey({ q: h.q + d.q, r: h.r + d.r }))).filter(
      (i): i is number => i !== undefined,
    ),
  );

  // Boards that aren't centered on the origin (5-6 player shapes, islands) sort around their own center.
  const cx = vertices.reduce((n, v) => n + v.x, 0) / vertices.length;
  const cy = vertices.reduce((n, v) => n + v.y, 0) / vertices.length;
  const clockwiseFromTop = (e: number): number => {
    const [a, b] = edges[e]!.vertices;
    const mx = (vertices[a]!.x + vertices[b]!.x) / 2 - cx;
    const my = (vertices[a]!.y + vertices[b]!.y) / 2 - cy;
    const angle = Math.atan2(mx, -my);
    return angle < 0 ? angle + 2 * Math.PI : angle;
  };
  const coast = edges
    .map((_, i) => i)
    .filter((i) => edges[i]!.hexes.length === 1)
    .sort((a, b) => clockwiseFromTop(a) - clockwiseFromTop(b));

  return { hexes: hexes.slice(), hexVertices, hexEdges, hexNeighbors, vertices, edges, coast, spiral: spiralOrder(hexes, hexIndex) };
}

/**
 * The classic inward spiral for a centered disk (the fixed layout depends on
 * it); otherwise outer hexes first, clockwise from the top.
 */
function spiralOrder(hexes: readonly Hex[], hexIndex: Map<string, number>): number[] {
  const radius = Math.max(...hexes.map((h) => hexDistance(h, { q: 0, r: 0 })));
  if (hexes.length === hexesInRadius(radius).length && hexIndex.has('0,0')) {
    return diskSpiral(radius, hexIndex);
  }
  const centers = hexes.map((h) => hexToPixel(h, 1));
  const cx = centers.reduce((n, c) => n + c.x, 0) / centers.length;
  const cy = centers.reduce((n, c) => n + c.y, 0) / centers.length;
  const keyed = centers.map((c, i) => {
    const angle = Math.atan2(c.x - cx, -(c.y - cy));
    return { i, dist: Math.round(Math.hypot(c.x - cx, c.y - cy) * 2), angle: angle < 0 ? angle + 2 * Math.PI : angle };
  });
  keyed.sort((a, b) => b.dist - a.dist || a.angle - b.angle);
  return keyed.map((k) => k.i);
}

function diskSpiral(radius: number, hexIndex: Map<string, number>): number[] {
  const spiral: number[] = [];
  for (let k = radius; k >= 1; k--) {
    const start = HEX_DIRECTIONS[4]!;
    let h: Hex = { q: start.q * k, r: start.r * k };
    for (let side = 0; side < 6; side++) {
      for (let step = 0; step < k; step++) {
        spiral.push(hexIndex.get(hexKey(h))!);
        const d = HEX_DIRECTIONS[side]!;
        h = { q: h.q + d.q, r: h.r + d.r };
      }
    }
  }
  spiral.push(hexIndex.get(hexKey({ q: 0, r: 0 }))!);
  return spiral;
}

/** The 19-hex base island, in the index order every pre-existing game uses. */
export const STANDARD_HEXES: readonly Hex[] = hexesInRadius(2);

const cache = new Map<string, Topology>();

/** Topology for a set of land hexes. Memoized: boards are cloned per action, so key by shape. */
export function topologyFor(hexes: readonly Hex[]): Topology {
  const key = hexes.map(hexKey).join(';');
  let t = cache.get(key);
  if (!t) {
    t = buildTopology(hexes);
    cache.set(key, t);
  }
  return t;
}

/** Topology of a game's board. Boards saved before per-game shapes have no hex list: they're the base island. */
export function topologyOf(state: { board: { hexes?: readonly Hex[] } }): Topology {
  return topologyFor(state.board.hexes ?? STANDARD_HEXES);
}

/** The base island's topology, for tests and previews that don't have a game. */
export const TOPOLOGY: Topology = topologyFor(STANDARD_HEXES);

export function hexIndexOf(h: Hex, t: Topology = TOPOLOGY): number {
  const i = t.hexes.findIndex((x) => x.q === h.q && x.r === h.r);
  if (i < 0) throw new Error(`hex ${hexKey(h)} is not on the board`);
  return i;
}

export function edgeBetween(a: number, b: number, t: Topology = TOPOLOGY): number | null {
  const e = t.vertices[a]?.edges.find((id) => t.edges[id]!.vertices.includes(b));
  return e ?? null;
}

export function otherEnd(edge: number, vertex: number, t: Topology = TOPOLOGY): number {
  const [a, b] = t.edges[edge]!.vertices;
  return a === vertex ? b : a;
}
